/**
 * This file provides implementations for common cookie operations directly on the document's cookie string.
 */

import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';

/**
 * Custom defined cookies.
 */
export const COOKIE_NAMES = {
    /**
     * Id token for the authenticated user to link their Hubspot chat session in CoPilot with Hubspot.
     *
     * https://developers.hubspot.com/docs/api-reference/conversations-visitor-identification-v3/guide
     */
    HS_CONVERSATIONS_COOKIE: 'hs_conversations_api_identity_token',
} as const;

type Cookie = (typeof COOKIE_NAMES)[keyof typeof COOKIE_NAMES];

type SetCookieOptions = {
    /**
     * For how long should this cookie persist before expiring.
     */
    maxAgeSeconds?: number;
    /**
     * Restricts the cookie to requests whose URL starts with this path.
     */
    path?: string;
    /**
     * Controls whether and how the cookie is attached to cross-site navigations.
     */
    sameSite?: 'Lax' | 'Strict' | 'None';
    /**
     * Forces the cookie to be transmitted only over HTTPS connections.
     */
    secure?: boolean;
    /**
     * Marks the cookie as partitioned so CHIPS-enabled embeds isolate its storage.
     */
    partitioned?: boolean;
};

/**
 * Retrieves and decodes a stored cookie by name, or returns undefined when it is absent or
 * executed outside of a browser environment.
 *
 * @param name The cookie identifier to look up.
 * @returns The decoded cookie contents, or undefined.
 */
export function getCookie(name: Cookie): string | undefined {
    if (isNil(document)) {
        return undefined;
    }

    const cookieMap = document.cookie.split('; ').reduce<Record<string, string>>((acc, pair) => {
        if (!pair) {
            return acc;
        }

        const [key, ...rest] = pair.split('=');
        if (!key) {
            return acc;
        }

        const value = rest.length > 0 ? rest.join('=') : '';
        acc[key] = decodeURIComponent(value);
        return acc;
    }, {});

    return cookieMap[name];
}

/**
 * Writes a cookie value with optional scoping, expiry, and security attributes. The operation is
 * skipped when executed without access to the DOM.
 *
 * @param name Cookie identifier to mutate.
 * @param value Raw (unencoded) cookie value to store.
 * @param options Optional attributes controlling expiration, scope, and transport guarantees.
 */
export function setCookie(
    name: Cookie,
    value: string,
    { maxAgeSeconds, path = '/', sameSite = 'Lax', secure, partitioned }: SetCookieOptions = {}
): void {
    if (isNil(document)) {
        return;
    }

    let cookie = `${name}=${encodeURIComponent(value)}`;
    if (isNumber(maxAgeSeconds)) {
        cookie += `; Max-Age=${Math.floor(maxAgeSeconds)}`;
    }
    cookie += `; Path=${path}`;
    if (sameSite) cookie += `; SameSite=${sameSite}`;
    if (secure || sameSite === 'None') cookie += '; Secure';
    if (partitioned) cookie += '; Partitioned';
    document.cookie = cookie;
}

/**
 * Removes the provided cookie by assigning an immediate-expiry value. No-ops when executed without
 * the DOM.
 *
 * @param name Cookie identifier to delete.
 * @param path Scope that matches the cookie's path attribute (defaults to root).
 */
export function deleteCookie(name: Cookie, path = '/'): void {
    if (isNil(document)) {
        return;
    }

    document.cookie = `${name}=; Path=${path}; Max-Age=0`;
}
